import { useEffect, useMemo } from 'react';
import { connect } from 'redux-bundler-react';

import StepHeader from '@components/step-header/StepHeader';
import DatePicker from '@components/date-picker/DatePicker';
import TextAreaInput from '@components/textarea/TextArea';
import SelectInput from '@components/select/Select';
import Tooltip from '@components/tooltip/tooltip';
import ExternalLink from '@components/external-link/ExternalLink';
import { useRequest } from '@stores';
import { useResetDirtyFields } from '@hooks';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';

import { PreAppRequestTypes } from '@src/utils/enums';
import { dateBeforeB } from '@src/utils/helpers';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import Checkbox from '@components/checkbox/Checkbox';
import { Label } from '@trussworks/react-uswds';
import { getPreAppDefaultValues, preAppSchema, getPreAppPayload } from './preApp.validation';

export const PreAppInformationMetadata = {
  header: 'Pre-application/Project Scoping Request Form',
  sectionName: 'Pre-application Information',
};

const yesNoUnsureOptions = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
  { value: `I am not sure`, label: `I'm not sure` },
];

const preAppRequestOptions = [
  { value: PreAppRequestTypes.StandardPermit, label: 'Standard Permit' },
  { value: PreAppRequestTypes.MitigationBankOrFee, label: 'Mitigation bank or in-lieu fee pre-application meeting' },
  { value: PreAppRequestTypes.ScopingResponse, label: 'Scoping response for environmental compliance' },
  { value: PreAppRequestTypes.NeedAdvice, label: 'I need advice on what type of permit is applicable to my project' },
];

const applicableLawCheckboxes = [
  {
    id: 'applicableLaws-isSpeciesPresent',
    value: 'isSpeciesPresent',
    label: 'Federally listed/proposed threatened/endangered species or habitat may be present on site',
    triggerField: 'speciesDetails',
  },
  {
    id: 'applicableLaws-isGovResourceConcern',
    value: 'isGovResourceConcern',
    label: 'Known tribal government resources issues',
    triggerField: 'govResourceConcernDetails',
  },
  {
    id: 'applicableLaws-isHistoricalResourceConcern',
    value: 'isHistoricalResourceConcern',
    label: 'Known cultural or historical resource issue',
    triggerField: 'historicalResourceConcernDetails',
  },
  {
    id: 'applicableLaws-isSection408_Required',
    value: 'isSection408_Required',
    label: 'Known Section 408 (USACE projects) permission required',
    triggerField: 'section408_Details',
  },
];

const FormSelect = ({ name, label, options, ...props }) => (
  <div className='margin-left-1'>
    <SelectInput name={name} label={label} required className='width-50' {...props}>
      {options.map(({ value, label }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </SelectInput>
  </div>
);

const ApplicableLawsSection = ({ isReadOnly, applicableLaws, trigger, handleChange }) => (
  <fieldset className='margin-left-1 width-50 padding-0' id='applicableLaws'>
        
    <Label className='required' htmlFor='applicableLaws'>
            <span id='applicableLaws_label'>Select any applicable laws (at least one required)</span>
          
    </Label>
        
    {applicableLawCheckboxes.map(({ id, value, label, triggerField }) => (
      <Checkbox
        key={id}
        disabled={isReadOnly}
        id={id}
        label={label}
        name='applicableLaws'
        onChange={(e) => {
          handleChange(e);
          trigger(triggerField);
        }}
        value={value}
        tile
      />
    ))}
    {applicableLaws?.includes('isSpeciesPresent') && (
      <div className='margin-top-2'>
                
        <TextAreaInput
          name='speciesDetails'
          label='List any federally listed/proposed threatened/endangered species or habitat that may be present on site'
          required
          onChange={handleChange}
          readOnly={isReadOnly}
        />
              
      </div>
    )}
        
    {applicableLaws?.includes('isGovResourceConcern') && (
      <div className='margin-top-2'>
                
        <TextAreaInput
          name='govResourceConcernDetails'
          label='Describe any known tribal government resource concerns or issues'
          required
          onChange={handleChange}
          readOnly={isReadOnly}
        />
              
      </div>
    )}
        
    {applicableLaws?.includes('isHistoricalResourceConcern') && (
      <div className='margin-top-2'>
                
        <TextAreaInput
          name='historicalResourceConcernDetails'
          label='Describe any known cultural or historical resource issues'
          required
          onChange={handleChange}
          readOnly={isReadOnly}
        />
              
      </div>
    )}
        
    {applicableLaws?.includes('isSection408_Required') && (
      <div className='margin-top-2'>
                
        <TextAreaInput
          name='section408_Details'
          label='Describe any known Section 408 (USACE project) permission requirements'
          required
          onChange={handleChange}
          readOnly={isReadOnly}
        />
              
      </div>
    )}
      
  </fieldset>
);

const PreAppInformation = connect(
  'doUpdatePreAppRequest',
  'selectRequestAPIData',
  ({ doUpdatePreAppRequest, requestAPIData, stepNo }) => {
    const isReadOnly = useRequest((state) => state.isFormReadOnly);
    const section408helper = (
      <>
        <p>
          The U.S. Army Corps of Engineers (USACE) Section 408 program provides permission for people, towns, or
          businesses to alter or impact a USACE project, like a dam, levee, or waterway.
        </p>
        <p>
          Before any changes can be made, the Section 408 program reviews alterations to ensure they won’t harm the
          project or the public.
        </p>
        <p>
          You can find more information about the Section 408 at{' '}
          <>
            <ExternalLink
              content={'The U.S. Army Corps of Engineers (USACE) Section 408 page'}
              href='https://www.usace.army.mil/Missions/Civil-Works/Section408/'
            />
          </>
        </p>
      </>
    );

    const defaultValues = useMemo(
      () => getPreAppDefaultValues({ preAppData: requestAPIData?.request?.preApplications?.[0] }),
      [requestAPIData?.request?.preApplications]
    );

    const methods = useForm({
      resolver: yupResolver(preAppSchema),
      mode: 'onBlur',
      defaultValues,
    });
    const {
      formState: { errors, dirtyFields },
      watch,
      getValues,
      trigger,
      reset,
    } = methods;

    const { updateSection, isSectionTouched } = useRequest();
    const isDirty = Object.values(dirtyFields || {}).length > 0;
    const errorCount = Object.values(errors || {}).length;
    const isValid = errorCount === 0;
    const isTouched = isSectionTouched(stepNo);

    const preAppRequestType = watch('preAppRequestType');
    const isOtherApplicableLaw = watch('isOtherApplicableLaw');
    const applicableLaws = watch('applicableLaws');

    useEffect(() => {
      updateSection(stepNo, { dirty: isDirty, valid: isValid });
    }, [stepNo, isDirty, isValid, updateSection]);

    //Reset the dirty states of the fields after a save.
    useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    useEffect(() => {
      updateSection(stepNo, { valid: isReadOnly ? true : isValid });
    }, [isValid, updateSection, stepNo, isReadOnly]);

    // Reset defaultValues on save
    useEffect(() => {
      reset(defaultValues, {
        keepDirty: true,
        keepErrors: true,
      });
    }, [defaultValues, reset]);

    const handleChange = (e) => {
      trigger(e?.target?.name);

      if (!isReadOnly) {
        const values = getValues();
        doUpdatePreAppRequest(getPreAppPayload(values));
      }
    };

    return (
      <FormProvider {...methods}>
        {errors && isTouched && !isReadOnly && <ErrorSummary errors={errors} sectionNo={stepNo} />}
        <StepHeader text='Pre-application Information' />

        <>
          <FormSelect
            name='preAppRequestType'
            label='Pre-application Request Type'
            options={preAppRequestOptions}
            readOnly={isReadOnly}
            onChange={handleChange}
          />
        </>
        {preAppRequestType === PreAppRequestTypes.ScopingResponse && (
          <>
            <div className='margin-left-1'>
              <DatePicker
                className='width-50'
                name='scopingCommentsDueDate'
                label='Date Scoping Comments Due'
                min={dateBeforeB}
                required
                readOnly={isReadOnly}
                onChange={handleChange}
              />
            </div>
          </>
        )}
        <>
          <FormSelect
            name='isOtherLeadFederalAgency'
            label='Is there another lead Federal Agency?'
            options={yesNoUnsureOptions}
            readOnly={isReadOnly}
            onChange={handleChange}
          />
        </>
        <>
          <FormSelect
            name='isOtherApplicableLaw'
            label='Are any of the items listed below present?'
            options={yesNoUnsureOptions}
            readOnly={isReadOnly}
            onChange={handleChange}
          />
        </>
        {isOtherApplicableLaw !== 'Yes' && (
          <ul className='margin-top-1 padding-left-4'>
            <li>Endangered Species Act-subject species</li>
            <li>Tribal government resource concerns or issues</li>
            <li>Cultural or historical resource concerns or issues</li>
            <li>
              Section 408
              <Tooltip clickable header={'Section 408'} name='section-408' content={section408helper} /> (USACE project)
              permission required
            </li>
          </ul>
        )}
        {isOtherApplicableLaw === 'Yes' && (
          <ApplicableLawsSection
            isReadOnly={isReadOnly}
            applicableLaws={applicableLaws}
            trigger={trigger}
            handleChange={handleChange}
          />
        )}
      </FormProvider>
    );
  }
);

PreAppInformation.metadata = PreAppInformationMetadata;

export default PreAppInformation;
